import { useCallback, useEffect, useState } from "react";

import AddBoxRoundedIcon from "@mui/icons-material/AddBoxRounded";
import IndeterminateCheckBoxRoundedIcon from "@mui/icons-material/IndeterminateCheckBoxRounded";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import { IconButton, Input, MenuItem, Select, Typography } from "@mui/material";

import useEvent from "@/hooks/useEvent";
import useThemeData from "@/hooks/useThemeData";

import style from "./style.module.scss";

interface SearchPassengerProps {
  value: number;
  max: number;
  min: number;
  onChange: (val: number) => void;

  childValue: number;
  onChangeChild: (val: number) => void;
}

const SelectPassenger = (props: SearchPassengerProps) => {
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);

  const theme = useThemeData();

  const handleClose = useEvent(() => {
    setOpen(false);
  });

  const handleMouseEnter = useEvent(() => {
    setHover(true);
  });

  const handleMouseLeave = useEvent(() => {
    setHover(false);
  });

  const renderValue = useCallback(() => {
    return (
      <div className="flex item-center">
        <PersonOutlineOutlinedIcon
          fontSize="small"
          sx={{ color: (theme) => theme.palette.text.secondary, marginRight: "8px" }}
        />

        <Typography color="textSecondary" variant="body2">
          {Number(props.value) + Number(props.childValue)}
        </Typography>
      </div>
    );
  }, [props.value, props.childValue]);

  useEffect(() => {
    if (!open) {
      setHover(false);
    }
  }, [open]);

  const total = Number(props.value) + Number(props.childValue);

  return (
    <div>
      <Select
        size="small"
        open={open}
        onClose={handleClose}
        onOpen={() => setOpen(true)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        value={props.value}
        renderValue={renderValue}
        MenuProps={{
          anchorOrigin: { vertical: "bottom", horizontal: "left" },
          transformOrigin: { vertical: "top", horizontal: "left" },
        }}
        className={style.select}
        sx={{
          "& .MuiSelect-select": {
            display: "flex",
            alignItems: "center",
          },
          background: (theme) =>
            open ? theme.palette.action.selected : hover ? theme.palette.action.hover : "",
        }}
      >
        <MenuItem
          disableTouchRipple
          sx={{
            paddingY: 0,
            "&:hover": { backgroundColor: "inherit" },
            "&.Mui-selected": { backgroundColor: "inherit !important" },
          }}
          value={props.value}
        >
          <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
            <Typography variant="body2" sx={{ width: "70px" }}>
              Adults
            </Typography>

            <IconButton
              aria-label="add"
              color="primary"
              disabled={total <= props.min || props.value <= 0}
              onClick={() => props.onChange(Math.max(props.value - 1, 0))}
            >
              <IndeterminateCheckBoxRoundedIcon />
            </IconButton>

            <Input
              sx={{ width: 25 }}
              value={props.value}
              onInput={(e) => {
                const currentValue = e.target.value;
                if (!currentValue) {
                  props.onChange("");
                }
                if (
                  /^\d$/.test(currentValue[currentValue.length - 1]) &&
                  Number(currentValue[currentValue.length - 1]) + props.childValue <= props.max &&
                  Number(currentValue[currentValue.length - 1]) + props.childValue >= props.min
                ) {
                  props.onChange(Number(currentValue[currentValue.length - 1]));
                }
              }}
              inputProps={{
                "aria-label": "description",
                sx: {
                  textAlign: "center",
                },
              }}
            />

            <IconButton
              aria-label="reduce"
              color="primary"
              disabled={total >= props.max}
              onClick={() => props.onChange(props.value + 1)}
            >
              <AddBoxRoundedIcon />
            </IconButton>
          </div>
        </MenuItem>

        <MenuItem
          disableTouchRipple
          sx={{
            paddingY: 0,
            "&:hover": { backgroundColor: "inherit" },
            "&.Mui-selected": { backgroundColor: "inherit !important" },
          }}
          value={props.childValue}
        >
          <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
            <div className="w-[70px]">
              <Typography variant="body2" sx={{ lineHeight: 1 }}>
                Children
              </Typography>
              <Typography
                variant="body2"
                fontSize={12}
                sx={{
                  lineHeight: 1,
                  marginTop: "4px",
                  color: theme.isDarkMode ? "#9aa0a6" : "#70757a",
                }}
              >
                aged 2-11
              </Typography>
            </div>

            <IconButton
              aria-label="add"
              color="primary"
              disabled={total <= props.min || props.childValue <= 0}
              onClick={() => props.onChangeChild(Math.max(props.childValue - 1, 0))}
            >
              <IndeterminateCheckBoxRoundedIcon />
            </IconButton>

            <Input
              sx={{ width: 25 }}
              value={props.childValue}
              onInput={(e) => {
                const currentValue = e.target.value;
                if (!currentValue) {
                  props.onChange("");
                }
                if (
                  /^\d$/.test(currentValue[currentValue.length - 1]) &&
                  Number(currentValue[currentValue.length - 1]) + props.value <= props.max &&
                  Number(currentValue[currentValue.length - 1]) + props.value >= props.min
                ) {
                  props.onChangeChild(Number(currentValue[currentValue.length - 1]));
                }
              }}
              inputProps={{
                "aria-label": "description",
                sx: {
                  textAlign: "center",
                },
              }}
            />

            <IconButton
              aria-label="reduce"
              color="primary"
              disabled={total >= props.max}
              onClick={() => props.onChangeChild(props.childValue + 1)}
            >
              <AddBoxRoundedIcon />
            </IconButton>
          </div>
        </MenuItem>
      </Select>
    </div>
  );
};

export default SelectPassenger;
